import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';

const toCleanMac = (mac) => (mac ? mac.replace(/[^0-9A-Fa-f]/g, '').toUpperCase() : '');
const DATA_WINDOW_MINUTES = 10;

// Get 3-tier gateway status matching SensorCard logic:
// - online (green): data within 15 minutes
// - warning (yellow): data between 15-60 minutes
// - offline (red): data older than 60 minutes
const getGatewayStatus = (minutesAgo) => {
  if (minutesAgo === null || minutesAgo === undefined) return 'offline';
  if (minutesAgo < 0) return 'online'; // Future time (clock skew), treat as online
  if (minutesAgo <= 15) return 'online';  // Within 15 min = online
  if (minutesAgo <= 60) return 'warning'; // 15-60 min = stale/warning
  return 'offline'; // > 60 min = offline
};

// Check gateway data status (same logic as GatewayDetailPage)
const checkGatewayDataStatus = async (gatewayData) => {
  if (!gatewayData?.mac_address) return { status: 'offline', minutesAgo: null };

  try {
    const cleanMac = toCleanMac(gatewayData.mac_address);
    if (!cleanMac) return { status: 'offline', minutesAgo: null };

    let latestTs = gatewayData.last_seen_at ? new Date(gatewayData.last_seen_at) : null;

    // Check user_raw_gateway_data
    try {
      const { data: latestUser } = await supabase
        .from('user_raw_gateway_data')
        .select('received_at')
        .eq('gateway_mac', cleanMac)
        .order('received_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (latestUser?.received_at) {
        const t = new Date(latestUser.received_at);
        latestTs = !latestTs || t > latestTs ? t : latestTs;
      }
    } catch (e) {
      console.warn('user raw gateway status check failed', e);
    }

    if (latestTs) {
      const minutesAgo = Math.floor((Date.now() - latestTs.getTime()) / 60000);
      const status = getGatewayStatus(minutesAgo);
      return { status, minutesAgo, lastDataReceived: latestTs, isConnected: status !== 'offline' };
    }

    return { status: 'offline', minutesAgo: null, isConnected: false };
  } catch (error) {
    console.error('Error checking gateway data status:', error);
    return { status: 'offline', minutesAgo: null, isConnected: false };
  }
};

export const useGateways = () => {
  const [gateways, setGateways] = useState([]);
  const [gatewaysCount, setGatewaysCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();

  const fetchGateways = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setGateways([]);
      setGatewaysCount(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch gateways
      const { data: gatewaysData, error: gatewaysError } = await supabase
        .from('gateways')
        .select('*')
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (gatewaysError) {
        throw gatewaysError;
      }

      if (!gatewaysData || gatewaysData.length === 0) {
        setGateways([]);
        setGatewaysCount(0);
        setIsLoading(false);
        return;
      }

      // Skip gateway health summary since table doesn't exist
      const healthMap = {}; // Empty health map

      // Enrich gateways with health data and calculate connected devices count
      const enrichedGateways = await Promise.all(
        gatewaysData.map(async (gateway) => {
          const health = healthMap[gateway.id] || {};
          
          // Get sensors assigned to this gateway
          const { data: sensorsData } = await supabase
            .from('sensors')
            .select('id')
            .eq('user_id', user.id)
            .eq('gateway_id', gateway.id);

          const sensorIds = sensorsData?.map(s => s.id) || [];
          const totalSensors = sensorIds.length;

          // Check sensor online/offline/warning status from gateway_sensor_readings
          // Using 3-tier logic matching GatewayDetailPage: online (<15min), warning (15-60min), offline (>60min)
          let onlineCount = 0;
          let warningCount = 0;
          let offlineCount = 0;
          let gatewaySource = null; // Track the connection source (mqtt/http)

          if (totalSensors > 0 && gateway.mac_address) {
            // Skip gateway_sensor_readings since table doesn't exist - use basic status only
            const cleanMac = toCleanMac(gateway.mac_address);
            if (cleanMac) {
              // Try to get basic gateway status from last_seen_at
              const lastSeen = gateway.last_seen_at ? new Date(gateway.last_seen_at) : null;
              const minutesAgo = lastSeen ? Math.floor((Date.now() - lastSeen.getTime()) / 60000) : null;
              
              // Simple status based on last_seen_at
              if (minutesAgo !== null) {
                if (minutesAgo <= 15) {
                  onlineCount = totalSensors; // All sensors assumed online
                } else if (minutesAgo <= 60) {
                  onlineCount = Math.floor(totalSensors * 0.5); // Half online
                  warningCount = totalSensors - onlineCount;
                } else {
                  offlineCount = totalSensors; // All offline
                }
              } else {
                offlineCount = totalSensors;
              }
            } else {
              offlineCount = totalSensors;
            }
          }

          // Check gateway data status (same logic as detail page)
          const dataStatus = await checkGatewayDataStatus(gateway);
          
          // Use 3-tier status from data flow check: online/warning/offline (matches SensorCard)
          const status = dataStatus.status;
          
          // Calculate last seen time - prefer from data status, fallback to health/gateway
          const lastSeenAt = dataStatus.lastDataReceived 
            ? dataStatus.lastDataReceived.toISOString()
            : (health.last_seen_at || gateway.last_seen_at || gateway.last_seen);

          // Skip gateway_sensor_readings since table doesn't exist
          let finalSource = gatewaySource || 'unknown';

          return {
            ...gateway,
            status, // 3-tier status: 'online' | 'warning' | 'offline'
            last_seen_at: lastSeenAt,
            minutesAgo: dataStatus.minutesAgo, // For debugging/display
            connectedDevices: totalSensors,
            onlineDevices: onlineCount,
            warningDevices: warningCount, // Stale devices (15-60 min)
            offlineDevices: offlineCount,
            source: finalSource, // mqtt or http
            health: {
              firmware_version: health.firmware_version,
              hardware_version: health.hardware_version,
              uptime_seconds: health.uptime_seconds,
              cpu_load: health.cpu_load,
              temperature: health.temperature,
              memory_free: health.memory_free,
              wan_ip: health.wan_ip,
              low_voltage: health.low_voltage,
            }
          };
        })
      );

      setGateways(enrichedGateways);
      setGatewaysCount(enrichedGateways.length);
    } catch (err) {
      console.error('Error fetching gateways:', err);
      setError(err.message || 'Failed to fetch gateways');
      setGateways([]);
      setGatewaysCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    fetchGateways();

    // Set up real-time subscription for gateways
    if (isAuthenticated && user) {
      const subscription = supabase
        .channel('gateways')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'gateways',
            filter: `user_id=eq.${user.id}`
          },
          () => {
            fetchGateways();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [fetchGateways, isAuthenticated, user]);

  return {
    gateways,
    gatewaysCount,
    isLoading,
    error,
    refetch: fetchGateways
  };
};

